/**
 * Campaign System - TopBarTimer Component
 * Componente de barra fixa no topo com contador regressivo
 */

import { useEffect, useState } from 'react';
import type { Contador, TopBarTimerProps, TimeLeft } from './types';
import { getContadores, calculateTimeLeft, getTopBarTemaClasses } from './utils';

const DEFAULT_STORAGE_KEY = 'campaign_contadores';

export default function TopBarTimer({ storageKey = DEFAULT_STORAGE_KEY }: TopBarTimerProps) {
  const [contador, setContador] = useState<Contador | null>(null);
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null);

  useEffect(() => {
    const loadContador = async () => {
      try {
        const contadores = await getContadores(storageKey);

        const ativo = contadores.find(
          (c) => c.ativo && c.location === 'top-bar'
        );

        setContador(ativo || null);
      } catch (error) {
        console.error('Erro ao carregar contador top-bar:', error);
        setContador(null);
      }
    };

    loadContador();
  }, [storageKey]);

  useEffect(() => {
    if (!contador) {
      setTimeLeft(null);
      return;
    }

    const updateTimer = () => {
      const dataFim = contador.dataFim.toISOString();

      const recorrencia =
        contador.modoRecorrencia === 'sem-recorrencia'
          ? undefined
          : contador.modoRecorrencia;

      const time = calculateTimeLeft(dataFim, recorrencia);
      setTimeLeft(time);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [contador]);

  if (!contador || !timeLeft) {
    return null;
  }

  const bgClass = getTopBarTemaClasses(contador.tema || 'laranja');
  const textoBotao = contador.textoBotao || 'GARANTIR AGORA';
  const urlBotao = contador.urlBotao || '#contato';

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 ${bgClass} text-white py-3 px-6 shadow-lg`}>
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex flex-col md:flex-row items-center gap-4 flex-1">
          <div className="flex items-center gap-2">
            <i className="ri-alarm-warning-line text-2xl"></i>
            <span className="font-bold text-sm md:text-base whitespace-nowrap">
              {contador.titulo}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs md:text-sm font-medium whitespace-nowrap">EXPIRA EM:</span>
            <div className="flex gap-1">
              {timeLeft.dias > 0 && (
                <>
                  <div className="bg-white/20 backdrop-blur-sm rounded px-2 py-1 min-w-[40px] text-center">
                    <div className="text-lg md:text-xl font-bold leading-none">
                      {String(timeLeft.dias).padStart(2, '0')}
                    </div>
                    <div className="text-[10px] uppercase leading-none mt-0.5">dias</div>
                  </div>
                  <span className="text-xl font-bold self-center">:</span>
                </>
              )}

              <div className="bg-white/20 backdrop-blur-sm rounded px-2 py-1 min-w-[40px] text-center">
                <div className="text-lg md:text-xl font-bold leading-none">
                  {String(timeLeft.horas).padStart(2, '0')}
                </div>
                <div className="text-[10px] uppercase leading-none mt-0.5">hrs</div>
              </div>

              <span className="text-xl font-bold self-center">:</span>

              <div className="bg-white/20 backdrop-blur-sm rounded px-2 py-1 min-w-[40px] text-center">
                <div className="text-lg md:text-xl font-bold leading-none">
                  {String(timeLeft.minutos).padStart(2, '0')}
                </div>
                <div className="text-[10px] uppercase leading-none mt-0.5">min</div>
              </div>

              <span className="text-xl font-bold self-center">:</span>

              <div className="bg-white/20 backdrop-blur-sm rounded px-2 py-1 min-w-[40px] text-center">
                <div className="text-lg md:text-xl font-bold leading-none">
                  {String(timeLeft.segundos).padStart(2, '0')}
                </div>
                <div className="text-[10px] uppercase leading-none mt-0.5">seg</div>
              </div>
            </div>
          </div>
        </div>

        <a
          href={urlBotao}
          className="bg-white text-orange-600 hover:bg-orange-50 px-6 py-2.5 rounded-full font-bold text-sm md:text-base whitespace-nowrap transition-all duration-300 hover:scale-105 shadow-lg cursor-pointer"
        >
          {textoBotao}
        </a>
      </div>
    </div>
  );
}